import React, { createContext, Dispatch, SetStateAction, useContext, useState } from 'react';
import { WidgetTableDataItem } from 'types/machine-health';
import { WidgetType } from 'types/protein';

interface ColorItem {
  color: string;
  tag: string;
}

interface AxisData {
  isLeftSide: boolean;
}
const defaultAxisData: AxisData = { isLeftSide: true };

export interface UploadTagsImageData {
  id: string;
  imageSize: number | undefined;
  imageUrl: string | undefined;
  imageFile: File | null;
  markedForDeleted: boolean;
}

interface WidgetTableContextProps {
  isModalOpenById: string | null;
  localTableNames: {
    id: string;
    name: string;
  }[];
  localTableActive: {
    id: string;
    active: boolean;
  }[];
  localTableRows: WidgetTableDataItem[];
  modalTitle: string;
  modalType: WidgetType;
  setIsModalOpenById: Dispatch<SetStateAction<string | null>>;
  setLocalTableNames: Dispatch<
    SetStateAction<
      {
        id: string;
        name: string;
      }[]
    >
  >;
  setLocalTableActive: Dispatch<SetStateAction<{ id: string; active: boolean }[]>>;
  setLocalTableRows: Dispatch<SetStateAction<WidgetTableDataItem[]>>;
  setModalTitle: Dispatch<SetStateAction<string>>;
  setModalType: Dispatch<SetStateAction<WidgetType>>;
  setTagGroupParentId: Dispatch<SetStateAction<string | undefined>>;
  tagGroupParentId: string | undefined;
  selectedColorItem: ColorItem[];
  setSelectedColorItem: Dispatch<SetStateAction<ColorItem[]>>;
  updateSelectedColor: (
    color: string, 
    tag: string
  ) => void;
  axisData: AxisData;
  setAxisData: Dispatch<SetStateAction<AxisData>>;
  uploadTagsImage: UploadTagsImageData[];
  setUploadTagsImage: Dispatch<SetStateAction<UploadTagsImageData[]>>;
}

const WidgetTableContext = createContext<WidgetTableContextProps>({
  isModalOpenById: null,
  localTableActive: [],
  uploadTagsImage: [],
  localTableNames: [],
  localTableRows: [],
  modalTitle: '',
  modalType: WidgetType.None,
  setIsModalOpenById: () => undefined,
  setLocalTableActive: () => undefined,
  setLocalTableNames: () => undefined,
  setLocalTableRows: () => undefined,
  setModalTitle: () => undefined,
  setModalType: () => undefined,
  setTagGroupParentId: () => undefined,
  tagGroupParentId: undefined,
  selectedColorItem: [],
  setSelectedColorItem: () => undefined,
  updateSelectedColor: () => undefined,
  axisData: { isLeftSide: true },
  setAxisData: () => undefined,
  setUploadTagsImage: () => undefined,
});

interface ProviderProps {
  children?: React.ReactNode;
}

export const WidgetTableProvider: React.FC = ({ children }: ProviderProps) => {
  const [isModalOpenById, setIsModalOpenById] = useState<string | null>(null);
  const [localTableActive, setLocalTableActive] = useState<{ id: string; active: boolean }[]>([]);
  const [localTableRows, setLocalTableRows] = useState<WidgetTableDataItem[]>([]);
  const [localTableNames, setLocalTableNames] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState<WidgetType>(WidgetType.None);
  const [tagGroupParentId, setTagGroupParentId] = useState<string | undefined>();

  const [selectedColorItem, setSelectedColorItem] = useState<ColorItem[]>([]);
  const [axisData, setAxisData] = useState<AxisData>(defaultAxisData);
  const updateSelectedColor = (tag: string, color: string) => {
    setSelectedColorItem((prev) => {
      const existingItem = prev.find(item => item.tag === tag);
      if (existingItem) {
        return prev.map(item =>
          item.tag === tag ? { ...item, color } : item
        );
      }
      return [...prev, { tag, color }];
    });
  };
  
  const [uploadTagsImage, setUploadTagsImage] = useState<UploadTagsImageData[]>([]);

  return (
    <WidgetTableContext.Provider
      value={{
        isModalOpenById,
        localTableActive,
        localTableNames,
        localTableRows,
        modalTitle,
        modalType,
        setIsModalOpenById,
        setLocalTableActive,
        setLocalTableNames,
        setLocalTableRows,
        setModalTitle,
        setModalType,
        setTagGroupParentId,
        tagGroupParentId,
        setSelectedColorItem,
        selectedColorItem,
        updateSelectedColor,
        axisData,
        setAxisData,
        uploadTagsImage,
        setUploadTagsImage,
      }}
    >
      {children}
    </WidgetTableContext.Provider>
  );
};

export const useWidgetTableContext = (): WidgetTableContextProps => useContext(WidgetTableContext);
